import { useContext, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Crown,
  Heart,
  Mail,
  Loader2,
  ReceiptText,
  Send,
  Share2,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import CartContext from '../../Context/Cart/CartContext';
import TableSessionContext from '../../Context/TableSession/TableSessionContext';
import { formatCurrency } from '../../Utils';
import { createShoutout } from '../../services/shoutout';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

const makeJoinUrl = (sessionId, joinUrlOverride) => {
  const url = new URL(joinUrlOverride || window.location.href, window.location.origin);
  url.searchParams.set('session', sessionId);
  return url.toString();
};

export function JoinTableSheet({ open, onClose, onStarted, restaurantId, tableRef, requestedSessionId, accentColor }) {
  const platformBrand = usePlatformBrand();
  const { join, loading, error } = useContext(TableSessionContext);
  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const accountEmail = String(storedUser?.email || '').trim().toLowerCase();
  const [displayName, setDisplayName] = useState(localStorage.getItem('customer_name') || storedUser?.name || '');
  const [email, setEmail] = useState(accountEmail);
  const [attempted, setAttempted] = useState(false);
  const [settlementTiming, setSettlementTiming] = useState('end');
  const [starting, setStarting] = useState(false);
  const activeSessionConflict = attempted && String(error || '').toLowerCase().includes('group order is already active');

  useEffect(() => {
    if (open) {
      setAttempted(false);
      setSettlementTiming('end');
      setStarting(false);
      setDisplayName(localStorage.getItem('customer_name') || storedUser?.name || '');
      setEmail(accountEmail);
    }
  }, [accountEmail, open, storedUser?.name]);

  if (!open) return null;

  const handleJoin = async (event) => {
    event.preventDefault();
    setAttempted(true);
    const cleanEmail = email.trim().toLowerCase();
    if (!requestedSessionId && !/^\S+@\S+\.\S+$/.test(cleanEmail)) return;
    setStarting(true);
    const startedAt = Date.now();
    try {
      const joined = await join({
        restaurantId,
        tableRef,
        sessionId: requestedSessionId,
        displayName,
        customerId: localStorage.getItem('customer_id') || undefined,
        customerPhone: localStorage.getItem('customer_phone') || undefined,
        email: cleanEmail || undefined,
        settlementTiming: requestedSessionId ? undefined : settlementTiming,
      });
      localStorage.setItem('customer_name', displayName.trim());
      if (cleanEmail) localStorage.setItem('customer_email', cleanEmail);
      const remainingAnimation = Math.max(0, 900 - (Date.now() - startedAt));
      if (remainingAnimation) await new Promise((resolve) => window.setTimeout(resolve, remainingAnimation));
      if (!requestedSessionId && joined?.emailSent) {
        toast.success(`Group link sent to ${cleanEmail}`);
      } else if (!requestedSessionId) {
        toast('Your group is ready. Use Share link to invite people.');
      }
      if (!requestedSessionId && joined?.participant?.is_host) onStarted?.();
      onClose();
    } catch {
      // The context exposes the customer-facing error below.
      setStarting(false);
    }
  };

  if (starting) {
    return (
      <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/60 sm:items-center sm:p-5">
        <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-[#faf9f6] sm:h-[640px] sm:max-w-md sm:rounded-2xl sm:shadow-2xl">
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-8 text-center">
            <div className="relative flex h-24 w-24 items-center justify-center">
              <span className="absolute inset-0 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-950" />
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
                <Users className="h-6 w-6 text-neutral-900" />
              </span>
            </div>
            <p className="mt-8 text-[12px] font-semibold uppercase text-neutral-500">Table {tableRef}</p>
            <h2 className="mt-2 text-[27px] font-semibold leading-tight tracking-tight text-neutral-950">
              {requestedSessionId ? 'Joining the group order' : 'Setting up your group order'}
            </h2>
            <p className="mt-3 max-w-[290px] text-[14px] leading-6 text-neutral-600">
              {requestedSessionId
                ? `Adding ${displayName.trim()} to Table ${tableRef}.`
                : `Getting Table ${tableRef} ready for ${displayName.trim()} and the group.`}
            </p>
            <div className="mt-8 flex items-center gap-2" aria-label="Please wait">
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{ backgroundColor: dot === 0 ? accentColor : '#d4d4d4', animationDelay: `${dot * 180}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/60 sm:items-center sm:p-5">
      <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-[#faf9f6] sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:rounded-2xl sm:shadow-2xl">
        <header className="grid h-16 shrink-0 grid-cols-[44px_1fr_44px] items-center border-b border-neutral-300 bg-white px-3">
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-lg text-neutral-700 hover:bg-neutral-100" aria-label="Back to menu">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 text-center">
            <h2 className="truncate text-[16px] font-semibold text-neutral-950">
              {requestedSessionId ? 'Join group order' : 'Start group order'}
            </h2>
            <p className="mt-0.5 text-[11px] font-semibold text-neutral-600">Table {tableRef}</p>
          </div>
          <span aria-hidden="true" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-24 pt-7 sm:px-6 sm:pb-7">
          <div className="mx-auto w-full max-w-sm">
            {activeSessionConflict ? (
              <div className="pt-3 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-neutral-800">
                  <Users className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-[24px] font-semibold leading-tight tracking-tight text-neutral-950">A group order is already open</h3>
                <p className="mt-3 text-[14px] leading-6 text-neutral-700">
                  You&apos;ll need an invite link or QR code from someone already in the group.
                </p>
                <button type="button" onClick={onClose} className="mt-7 h-12 w-full rounded-xl bg-neutral-950 text-[14px] font-semibold text-white" style={{ backgroundColor: '#171717' }}>
                  Back to menu
                </button>
              </div>
            ) : (
              <>
            <h3 className="max-w-[340px] text-[26px] font-semibold leading-[1.15] tracking-tight text-neutral-950">
              {requestedSessionId ? 'Join the group at this table' : 'Order together as a group'}
            </h3>
            <p className="mt-3 max-w-[350px] text-[15px] leading-6 text-neutral-800">
              {requestedSessionId
                ? `Add your items to Table ${tableRef}. Everything you choose will stay under your name.`
                : `Start a group order for Table ${tableRef}, then invite everyone to choose from their own phone.`}
            </p>

            {!requestedSessionId && (
              <>
                <div className="mt-6">
                  <p className="text-[14px] font-bold text-neutral-950">You&apos;re starting the group</p>
                  <p className="mt-1.5 max-w-[350px] text-[13px] leading-5 text-neutral-700">
                    Invite people, see what everyone has chosen, and send the order when the group is ready.
                  </p>
                </div>
                <fieldset className="mt-6">
                  <legend className="text-[14px] font-bold text-neutral-950">When would you like to pay?</legend>
                  <div className="mt-3 space-y-2">
                    {[
                      { value: 'end', title: 'Pay at the end', note: 'Keep adding orders, then pay the full balance when everyone is finished.' },
                      { value: 'per_round', title: 'Pay after each order', note: 'Send an order, pay for it, then add another when you are ready.' },
                    ].map((option) => {
                      const selected = settlementTiming === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSettlementTiming(option.value)}
                          className={`flex w-full items-start justify-between gap-4 rounded-xl px-3.5 py-3.5 text-left transition-colors ${selected ? 'bg-neutral-950' : 'bg-neutral-100'}`}
                          style={selected ? { backgroundColor: '#171717' } : undefined}
                        >
                          <span className="pr-2">
                            <span className={`block text-[15px] font-semibold ${selected ? 'text-white' : 'text-neutral-950'}`}>{option.title}</span>
                            <span className={`mt-1 block text-[13px] leading-5 ${selected ? 'text-white/70' : 'text-neutral-700'}`}>{option.note}</span>
                          </span>
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                            {selected && <Check className="h-4 w-4" style={{ color: accentColor }} strokeWidth={3} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              </>
            )}

            <form onSubmit={handleJoin} className="mt-7">
              <label htmlFor="table-display-name" className="text-[14px] font-bold text-neutral-950">Your name at the table</label>
              <p className="mt-1 text-[12px] leading-5 text-neutral-700">Everyone in the group will see this name beside your items.</p>
              <input
                id="table-display-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value.slice(0, 40))}
                placeholder="e.g. Gilbert"
                autoFocus
                className="mt-3 h-12 w-full rounded-xl border border-neutral-500 bg-white px-4 text-[15px] font-medium text-neutral-950 outline-none placeholder:text-neutral-500 focus:border-neutral-950"
              />
              {!requestedSessionId && (
                <div className="mt-5">
                  <label htmlFor="table-host-email" className="text-[14px] font-bold text-neutral-950">Email</label>
                  <p className="mt-1 text-[12px] leading-5 text-neutral-700">
                    We will send your group link here so you can return to the order.
                  </p>
                  <div className="relative mt-3">
                    <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                    <input
                      id="table-host-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value.slice(0, 254))}
                      placeholder="you@example.com"
                      autoComplete="email"
                      readOnly={Boolean(accountEmail)}
                      required
                      className="h-12 w-full rounded-xl border border-neutral-500 bg-white pl-11 pr-4 text-[15px] font-medium text-neutral-950 outline-none placeholder:text-neutral-500 focus:border-neutral-950 read-only:bg-neutral-100"
                    />
                  </div>
                  {accountEmail && (
                    <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-neutral-700">
                      <Check className="h-3.5 w-3.5" style={{ color: accentColor }} /> Linked to your {platformBrand.name} account
                    </p>
                  )}
                  {attempted && !/^\S+@\S+\.\S+$/.test(email.trim()) && (
                    <p className="mt-2 text-[13px] text-red-700">Enter a valid email address.</p>
                  )}
                </div>
              )}
              {attempted && error && <p className="mt-2 text-[13px] text-red-700">{error}</p>}
              <button
                type="submit"
                disabled={loading || starting || !displayName.trim() || (!requestedSessionId && !/^\S+@\S+\.\S+$/.test(email.trim()))}
                className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[14px] font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: accentColor }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                {requestedSessionId ? 'Join group' : 'Start group order'}
              </button>
            </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SharedTablePanel({ open, onClose, accentColor, tableRef, joinUrlOverride = '', initialView = 'main' }) {
  const platformBrand = usePlatformBrand();
  const {
    session,
    participant,
    joinUrl,
    recoveryUrl,
    syncing,
    syncDraft,
    submit,
    requestSettlement,
    removeParticipant,
  } = useContext(TableSessionContext);
  const { cartItems, clearCart } = useContext(CartContext);
  const [view, setView] = useState('main');
  const [mainTab, setMainTab] = useState('members');
  const [expandedParticipantId, setExpandedParticipantId] = useState(null);
  const [workingAction, setWorkingAction] = useState('');
  const [settlementMode, setSettlementMode] = useState('together');
  const [selectedPaymentItems, setSelectedPaymentItems] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [shoutoutRating, setShoutoutRating] = useState(5);
  const [shoutoutMessage, setShoutoutMessage] = useState('');
  const [shoutoutResult, setShoutoutResult] = useState(null);

  useEffect(() => {
    if (open) {
      setView(initialView);
      setMainTab('members');
      setShoutoutResult(null);
    }
  }, [initialView, open]);

  const participantGroups = useMemo(() => (session?.participants || []).map((person) => {
    const items = (session?.items || []).filter((item) => item.status !== 'removed' && item.participant_id === person.id);
    return {
      person,
      items,
      draftCount: items.filter((item) => item.status === 'draft').reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      total: items.reduce((sum, item) => sum + Number(item.total_price || 0), 0),
    };
  }), [session?.items, session?.participants]);

  const effectiveJoinUrl = session?.id
    ? (joinUrlOverride ? makeJoinUrl(session.id, joinUrlOverride) : (joinUrl || makeJoinUrl(session.id)))
    : '';
  const isHost = participant?.is_host;
  const ownDrafts = (session?.items || []).filter((item) => item.status === 'draft' && item.participant_id === participant?.id);
  const allDrafts = (session?.items || []).filter((item) => item.status === 'draft');
  const totalOwed = Number(session?.summary?.remaining || 0);
  const rounds = session?.orders || [];
  const hostParticipant = (session?.participants || []).find((person) => person.is_host);
  const shoutoutOrder = [...rounds].reverse().find((order) => (
    order.customer_id
    && (order.submitted_by_participant_id === participant?.id
      || (order.order_items || []).some((item) => item.table_session_participant_id === participant?.id))
  ));

  if (!open || !session || !participant) return null;

  const closePanel = () => {
    setView('main');
    setExpandedParticipantId(null);
    onClose();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(effectiveJoinUrl);
      toast.success('Group link copied');
    } catch {
      const input = document.createElement('textarea');
      input.value = effectiveJoinUrl;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (copied) toast.success('Group link copied');
      else toast.error('Could not copy the group link.');
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Join our group at Table ${tableRef}`, text: `Choose your food with us on ${platformBrand.name}.`, url: effectiveJoinUrl });
        return;
      }
      await copyLink();
    } catch {
      // Native share sheets can be dismissed without showing an error.
    }
  };

  const sendReturnLink = () => {
    if (!recoveryUrl || !isHost) return;
    const message = `My private ${platformBrand.name} return link for Table ${tableRef}. Keep this link private and use it only to reopen the group order.\n\n${recoveryUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const prepareShoutout = async (shareType) => {
    if (shoutoutResult?.shareUrl) return shoutoutResult;
    if (!shoutoutOrder?.id) {
      toast.error('A linked order is needed before you can give a Shout-Out.');
      return null;
    }

    setWorkingAction('shoutout');
    try {
      const firstItem = (shoutoutOrder.order_items || []).find((item) => item.item_name);
      const response = await createShoutout({
        restaurant_id: session.restaurant_id,
        order_id: shoutoutOrder.id,
        customer_id: shoutoutOrder.customer_id,
        message: shoutoutMessage.trim() || (firstItem ? `${firstItem.item_name} deserves a try.` : 'This place is worth discovering.'),
        rating: shoutoutRating,
        share_type: shareType,
        origin: window.location.origin,
      });
      if (!response?.success) throw new Error(response?.error || 'Could not prepare your Shout-Out.');
      setShoutoutResult(response.data);
      return response.data;
    } catch (actionError) {
      toast.error(actionError.message || 'Could not prepare your Shout-Out.');
      return null;
    } finally {
      setWorkingAction('');
    }
  };

  const shareShoutout = async () => {
    const shareWindow = window.open('', '_blank');
    const result = await prepareShoutout('whatsapp');
    if (!result?.shareUrl) {
      shareWindow?.close();
      return;
    }
    const placeName = session.restaurants?.name || 'this place';
    const message = `${participant.display_name} recommends ${placeName}.\n\n${shoutoutMessage.trim() || 'This place is worth discovering.'}\n\n${result.shareUrl}\n\nShared via ${platformBrand.name}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    if (shareWindow) shareWindow.location.href = whatsappUrl;
    else window.location.href = whatsappUrl;
  };

  const copyShoutout = async () => {
    const result = await prepareShoutout('copy');
    if (!result?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(result.shareUrl);
      toast.success('Shout-Out link copied');
    } catch {
      const input = document.createElement('textarea');
      input.value = result.shareUrl;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (copied) toast.success('Shout-Out link copied');
      else toast.error('Could not copy the Shout-Out link.');
    }
  };

  const handleSubmit = async (submitAll) => {
    setWorkingAction(submitAll ? 'all' : 'mine');
    try {
      await syncDraft(cartItems);
      await submit({ submitAll });
      clearCart();
      toast.success(submitAll ? 'Group order sent' : 'Your items were sent');
    } catch (actionError) {
      toast.error(actionError.message || 'Could not send these items.');
    } finally {
      setWorkingAction('');
    }
  };

  const handleSettlement = async () => {
    setWorkingAction('settle');
    try {
      await requestSettlement({
        splitType: settlementMode,
        orderItemIds: settlementMode === 'item' ? selectedPaymentItems : undefined,
        amount: settlementMode === 'amount' ? Number(paymentAmount || 0) : undefined,
      });
      toast.success('Payment request sent');
      setView('main');
    } catch (actionError) {
      toast.error(actionError.message || 'Could not send the payment request.');
    } finally {
      setWorkingAction('');
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setWorkingAction(`remove-${removeTarget.id}`);
    try {
      await removeParticipant(removeTarget.id);
      toast.success(`${removeTarget.display_name} was removed`);
      setRemoveTarget(null);
    } catch (actionError) {
      toast.error(actionError.message || 'Could not remove this guest.');
    } finally {
      setWorkingAction('');
    }
  };

  const statusLabel = session.status === 'settling'
    ? session.settlement_timing === 'per_round' ? 'Payment due' : 'Payment requested'
    : rounds.length > 0
      ? 'Orders are open'
      : 'Choosing items';

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/60 sm:items-center sm:p-5">
      <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-[#f8f7f3] shadow-2xl sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl">
        <header className="grid h-16 shrink-0 grid-cols-[40px_1fr_40px] items-center border-b border-neutral-300 bg-white px-3">
          <button
            type="button"
            onClick={() => view === 'main' ? closePanel() : setView('main')}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-100"
            aria-label={view === 'main' ? 'Back to menu' : 'Back to group summary'}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 text-center">
            <h2 className="truncate text-[16px] font-semibold text-neutral-950">
              {view === 'invite' ? 'Invite people' : view === 'settle' ? 'Choose how to pay' : view === 'shoutout' ? 'Give a Shout-Out' : 'Your group order'}
            </h2>
            <p className="mt-0.5 text-[11px] text-neutral-500">Table {tableRef}</p>
          </div>
          <button type="button" onClick={closePanel} className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-100" aria-label="Close group order">
            <X className="h-4 w-4" />
          </button>
        </header>

        {view === 'main' && (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto bg-white">
              <section className="border-b border-neutral-300 bg-[#faf9f6] px-5 pb-6 pt-6">
                <div className="text-center">
                  <div className="mb-5 flex items-center justify-center gap-2 text-[12px] font-semibold text-neutral-800">
                    {isHost ? <Crown className="h-3.5 w-3.5" style={{ color: accentColor }} /> : <Users className="h-3.5 w-3.5 text-neutral-500" />}
                    <span>{isHost ? 'You started this group' : `${hostParticipant?.display_name || 'Someone at the table'} started this group`}</span>
                    {participant?.has_account && (
                      <span className="rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[10px] text-neutral-600">{platformBrand.name} account</span>
                    )}
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-neutral-600">Total owed</p>
                    <p className="mt-1.5 text-[32px] font-semibold leading-none tracking-tight text-neutral-950">{formatCurrency(totalOwed)}</p>
                    {(session.summary?.paidTotal > 0 || allDrafts.length > 0) && (
                      <p className="mt-2.5 text-[12px] text-neutral-700">
                        {allDrafts.length > 0
                          ? `${session.summary?.draftItemCount || 0} item${session.summary?.draftItemCount === 1 ? '' : 's'} not sent`
                          : `${formatCurrency(session.summary.paidTotal)} paid`}
                      </p>
                    )}
                  </div>
                  {statusLabel !== 'Choosing items' && (
                    <span className={`mt-3 inline-flex min-h-8 items-center rounded-lg border px-2.5 text-[11px] font-semibold ${session.status === 'settling' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`}>
                      <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" />
                      {statusLabel}
                    </span>
                  )}
                </div>

                <div className={`mt-5 grid gap-2 ${isHost && rounds.length ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <button type="button" onClick={() => setView('invite')} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 text-[12px] font-semibold text-neutral-900 hover:bg-neutral-100">
                    <Share2 className="h-4 w-4" /> Share link
                  </button>
                  {isHost && rounds.length > 0 && (
                      <button type="button" onClick={() => setView('settle')} disabled={session.status === 'settling'} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 text-[12px] font-semibold text-neutral-900 hover:bg-neutral-100 disabled:opacity-50">
                        <ReceiptText className="h-4 w-4" /> {session.status === 'settling' ? 'Payment requested' : 'Choose payment'}
                      </button>
                  )}
                </div>
                {shoutoutOrder && (
                  <button type="button" onClick={() => setView('shoutout')} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 text-[12px] font-semibold text-neutral-900 hover:bg-neutral-100">
                    <Heart className="h-4 w-4" /> Give a Shout-Out
                  </button>
                )}
                {isHost && recoveryUrl && (
                  <button type="button" onClick={sendReturnLink} className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 text-[11px] font-semibold text-neutral-600 hover:text-neutral-950">
                    <Send className="h-3.5 w-3.5" /> Send my private return link to WhatsApp
                  </button>
                )}
              </section>

              <nav className="grid h-12 grid-cols-2 border-b border-neutral-300 bg-white px-5" aria-label="Group order sections">
                {[
                  { id: 'members', label: `People (${session.summary?.participantCount || 1})` },
                  { id: 'rounds', label: `Orders (${rounds.length})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setMainTab(tab.id)}
                    className={`relative h-12 text-[13px] font-semibold ${mainTab === tab.id ? 'text-neutral-950' : 'text-neutral-500'}`}
                  >
                    {tab.label}
                    {mainTab === tab.id && <span className="absolute inset-x-5 bottom-0 h-0.5" style={{ backgroundColor: accentColor }} />}
                  </button>
                ))}
              </nav>

              {mainTab === 'members' && <section className="divide-y divide-neutral-200">
                {isHost && (session.summary?.participantCount || 0) > 1 && (
                  <p className="bg-neutral-50 px-5 py-2.5 text-[11px] leading-4 text-neutral-600">Tap a guest to view their items or remove them from the group.</p>
                )}
                {participantGroups.map((group) => {
                  const expanded = expandedParticipantId === group.person.id;
                  const itemCount = group.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
                  return (
                    <div key={group.person.id}>
                      <button
                        type="button"
                        onClick={() => setExpandedParticipantId(expanded ? null : group.person.id)}
                        className={`grid w-full grid-cols-[40px_minmax(0,1fr)_auto_20px] items-center gap-3 px-5 py-4 text-left transition-colors ${group.person.is_host ? 'bg-[#f5f2ea] hover:bg-[#f0ece2]' : 'bg-white hover:bg-neutral-50'}`}
                      >
                        <span className={`flex h-10 w-10 items-center justify-center rounded-full text-[13px] font-bold uppercase ${group.person.is_host ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-800'}`}>
                          {group.person.is_host ? <Crown className="h-4 w-4" /> : String(group.person.display_name || '?').charAt(0)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[15px] font-semibold text-neutral-950">
                            {group.person.display_name}{group.person.id === participant.id ? ' - You' : ''}
                          </p>
                          <p className="mt-1 text-[12px] text-neutral-600">
                            {group.person.is_host ? 'Started the group - ' : ''}{itemCount} item{itemCount === 1 ? '' : 's'}
                            {group.draftCount > 0 ? ` - ${group.draftCount} not sent` : itemCount > 0 ? ' - Sent' : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[14px] font-semibold text-neutral-950">{formatCurrency(group.total)}</p>
                        </div>
                        {expanded ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-500" />}
                      </button>

                      {expanded && (
                        <div className="border-t border-neutral-200 bg-[#f5f4ef] px-5 py-3">
                          {group.items.length ? (
                            <div className="divide-y divide-neutral-200">
                              {group.items.map((item) => (
                                <div key={item.id} className="flex items-start justify-between gap-4 py-2.5">
                                  <div className="min-w-0">
                                    <p className="text-[13px] font-medium text-neutral-900">{item.quantity} x {item.item_name}</p>
                                    <p className="mt-0.5 text-[11px] capitalize text-neutral-600">{item.status === 'draft' ? 'Not sent yet' : `Order ${item.round_number || ''}`}</p>
                                    {item.notes && <p className="mt-1 text-[11px] text-neutral-500">{item.notes}</p>}
                                  </div>
                                  <span className="shrink-0 text-[12px] font-semibold text-neutral-700">{formatCurrency(item.total_price)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="py-2 text-[12px] text-neutral-500">No items selected yet.</p>
                          )}
                          {isHost && !group.person.is_host && (
                            <button type="button" onClick={() => setRemoveTarget(group.person)} className="mt-2 flex h-9 items-center gap-2 text-[11px] font-semibold text-red-600">
                              <Trash2 className="h-3.5 w-3.5" /> Remove from group
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>}

              {mainTab === 'rounds' && (
                <section className="divide-y divide-neutral-200 bg-white">
                  {rounds.length > 0 ? rounds.map((order) => {
                    const unpaid = Number(order.payment?.unpaid_amount || 0);
                    return (
                      <div key={order.id} className="flex items-center gap-3 px-5 py-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700">
                          <ReceiptText className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[14px] font-semibold text-neutral-950">Order {order.table_session_round}</p>
                          <p className="mt-1 truncate text-[11px] text-neutral-600">#{order.order_number} - {unpaid > 0 ? `${formatCurrency(unpaid)} due` : 'Paid'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[14px] font-semibold text-neutral-950">{formatCurrency(order.total_amount)}</p>
                          <p className="mt-1 text-[11px] capitalize text-neutral-600">{order.status}</p>
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="px-5 py-10 text-center text-[13px] text-neutral-600">No orders sent yet.</p>
                  )}
                </section>
              )}

              {syncing && <p className="px-5 py-3 text-center text-[11px] text-neutral-400">Saving your group...</p>}
            </div>

            <footer className="shrink-0 space-y-2 border-t border-neutral-300 bg-[#f8f7f3] px-5 py-4">
              {session.status !== 'settling' && allDrafts.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleSubmit(isHost)}
                  disabled={Boolean(workingAction) || (!isHost && ownDrafts.length === 0)}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[13px] font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: accentColor }}
                >
                  {workingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {isHost ? 'Send group order' : 'Send my items'}
                </button>
              )}
              {isHost && session.status !== 'settling' && ownDrafts.length > 0 && allDrafts.length > ownDrafts.length && (
                <button type="button" onClick={() => handleSubmit(false)} disabled={Boolean(workingAction)} className="h-10 w-full text-[12px] font-semibold text-neutral-700 disabled:opacity-50">
                  Send only my items
                </button>
              )}
              <button type="button" onClick={closePanel} className="h-12 w-full rounded-xl border border-neutral-300 bg-white text-[13px] font-semibold text-neutral-900">
                Continue ordering
              </button>
            </footer>
          </>
        )}

        {view === 'invite' && (
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-6">
            <div className="mx-auto max-w-sm text-center">
              <h3 className="text-[20px] font-semibold text-neutral-950">Invite people to join</h3>
              <p className="mt-2 text-[13px] leading-5 text-neutral-700">Let others scan this code or share the link. Everyone can choose items from their own phone.</p>
              <div className="mx-auto mt-6 w-fit rounded-2xl border border-neutral-300 bg-white p-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(effectiveJoinUrl)}`}
                  alt={`QR code to join Table ${tableRef}`}
                  className="h-48 w-48 rounded-lg sm:h-56 sm:w-56"
                />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button type="button" onClick={copyLink} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-neutral-400 bg-white text-[12px] font-semibold text-neutral-900">
                  <Copy className="h-4 w-4" /> Copy link
                </button>
                <button type="button" onClick={shareLink} className="flex h-11 items-center justify-center gap-2 rounded-xl text-[12px] font-semibold text-white" style={{ backgroundColor: accentColor }}>
                  <Share2 className="h-4 w-4" /> Share
                </button>
              </div>
              <button type="button" onClick={closePanel} className="mt-3 h-12 w-full rounded-xl border border-neutral-300 bg-[#f5f4ef] text-[13px] font-semibold text-neutral-950">
                Start ordering
              </button>
            </div>
          </div>
        )}

        {view === 'settle' && (
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-6">
            <div className="flex items-start justify-between gap-4 border-b border-neutral-300 pb-5">
              <div>
                <p className="text-[12px] text-neutral-500">Left to pay</p>
                <p className="mt-1 text-[30px] font-semibold text-neutral-950">{formatCurrency(session.summary?.remaining || 0)}</p>
              </div>
              {session.summary?.remaining <= 0 && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            </div>

            <div className="mt-5 grid grid-cols-4 gap-1 rounded-xl border border-neutral-300 bg-[#efeee9] p-1">
              {[
                ['together', 'One person'],
                ['person', 'By person'],
                ['item', 'By item'],
                ['amount', 'Amount'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSettlementMode(value)}
                  className={`h-10 rounded-lg text-[11px] font-semibold ${settlementMode === value ? 'text-white' : 'text-neutral-700'}`}
                  style={settlementMode === value ? { backgroundColor: '#171717' } : undefined}
                >
                  {label}
                </button>
              ))}
            </div>

            {settlementMode === 'together' && <p className="mt-4 text-[12px] leading-5 text-neutral-500">One person will pay the remaining balance.</p>}
            {settlementMode === 'person' && <p className="mt-4 text-[12px] leading-5 text-neutral-500">Each person&apos;s share is based on the items under their name.</p>}
            {settlementMode === 'item' && (
              <div className="mt-4 max-h-64 divide-y divide-neutral-200 overflow-y-auto border-y border-neutral-200">
                {rounds.flatMap((order) => order.order_items || []).map((item) => {
                  const checked = selectedPaymentItems.includes(item.id);
                  return (
                    <label key={item.id} className="flex cursor-pointer items-center justify-between gap-3 py-3 text-[13px]">
                      <span className="flex min-w-0 items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelectedPaymentItems((current) => checked ? current.filter((id) => id !== item.id) : [...current, item.id])}
                          className="h-4 w-4 rounded border-neutral-300"
                        />
                        <span className="truncate">{item.quantity} x {item.item_name}</span>
                      </span>
                      <span className="shrink-0 font-medium">{formatCurrency(item.total_price)}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {settlementMode === 'amount' && (
              <div className="mt-4">
                <label htmlFor="table-payment-amount" className="text-[12px] font-semibold text-neutral-700">Amount to pay now</label>
                <input
                  id="table-payment-amount"
                  type="number"
                  min="1"
                  max={session.summary?.remaining || 0}
                  value={paymentAmount}
                  onChange={(event) => setPaymentAmount(event.target.value)}
                  placeholder={`Up to ${session.summary?.remaining || 0}`}
                  className="mt-2 h-12 w-full rounded-xl border border-neutral-300 px-4 text-[14px] outline-none focus:border-neutral-600"
                />
              </div>
            )}

            <button type="button" onClick={handleSettlement} disabled={workingAction === 'settle' || session.status === 'settling' || session.summary?.remaining <= 0} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accentColor }}>
              {workingAction === 'settle' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
              {session.status === 'settling' ? 'Payment requested' : 'Send payment request'}
            </button>
            <p className="mt-3 text-center text-[11px] leading-4 text-neutral-500">You&apos;ll see an update here once the payment is confirmed.</p>
          </div>
        )}

        {view === 'shoutout' && shoutoutOrder && (
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-6">
            <div className="mx-auto max-w-sm">
              <h3 className="text-[21px] font-semibold leading-7 text-neutral-950">Share something you enjoyed</h3>
              <p className="mt-2 text-[13px] leading-5 text-neutral-600">Your Shout-Out is optional. Nothing is shared until you choose one of the buttons below.</p>

              <div className="mt-6">
                <p className="text-[13px] font-semibold text-neutral-800">Your rating</p>
                <div className="mt-2 flex gap-1">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setShoutoutRating(value)}
                      className="flex h-10 w-10 items-center justify-center text-[25px] leading-none"
                      style={{ color: value <= shoutoutRating ? accentColor : '#d4d4d4' }}
                      aria-label={`${value} star rating`}
                    >
                      &#9733;
                    </button>
                  ))}
                </div>
              </div>

              <label htmlFor="group-shoutout-message" className="mt-5 block text-[13px] font-semibold text-neutral-800">What stood out?</label>
              <textarea
                id="group-shoutout-message"
                value={shoutoutMessage}
                onChange={(event) => setShoutoutMessage(event.target.value.slice(0, 180))}
                placeholder="The food, service, or moment you would recommend"
                className="mt-2 min-h-28 w-full resize-none rounded-xl border border-neutral-300 bg-white px-3.5 py-3 text-[14px] leading-5 text-neutral-950 outline-none placeholder:text-neutral-400 focus:border-neutral-600"
              />
              <p className="mt-1 text-right text-[11px] text-neutral-400">{shoutoutMessage.length}/180</p>

              <div className="mt-5 grid grid-cols-2 gap-2">
                <button type="button" onClick={shareShoutout} disabled={workingAction === 'shoutout'} className="flex h-12 items-center justify-center gap-2 rounded-xl text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accentColor }}>
                  {workingAction === 'shoutout' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} WhatsApp
                </button>
                <button type="button" onClick={copyShoutout} disabled={workingAction === 'shoutout'} className="flex h-12 items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white text-[13px] font-semibold text-neutral-900 disabled:opacity-50">
                  <Copy className="h-4 w-4" /> Copy link
                </button>
              </div>
              <button type="button" onClick={() => setView('main')} className="mt-3 h-11 w-full text-[12px] font-semibold text-neutral-600">Not now</button>
            </div>
          </div>
        )}
      </div>

      {removeTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/65 p-5">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5">
            <h3 className="text-[17px] font-semibold text-neutral-950">Remove {removeTarget.display_name}?</h3>
            <p className="mt-2 text-[13px] leading-5 text-neutral-600">Their unsent selections will be removed. Anything already sent will stay in the group history.</p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setRemoveTarget(null)} className="h-11 rounded-xl border border-neutral-300 text-[13px] font-semibold text-neutral-800">Keep guest</button>
              <button type="button" onClick={confirmRemove} className="h-11 rounded-xl bg-red-600 text-[13px] font-semibold text-white">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SharedTableButton({ session, participant, onClick, idleLabel = 'Order together', className = '' }) {
  const guestCount = session
    ? Math.max(0, Number(session.summary?.participantCount || session.participants?.length || 1) - 1)
    : 0;
  const activeLabel = participant?.is_host
    ? (guestCount === 0 ? 'No guests yet' : `${guestCount} guest${guestCount === 1 ? '' : 's'}`)
    : `${session?.summary?.participantCount || session?.participants?.length || 1} joined`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={session ? `Open group order, ${activeLabel}` : idleLabel}
      className={`flex h-10 min-w-10 shrink-0 items-center justify-end gap-2 rounded-lg px-1 text-[12px] font-semibold text-neutral-700 transition hover:bg-black/5 active:bg-black/10 ${className}`}
    >
      {session && <span className="whitespace-nowrap">{activeLabel}</span>}
      {session && participant?.is_host && <Crown className="h-3 w-3 shrink-0 text-neutral-400" />}
      <ChevronRight className="h-4 w-4 shrink-0 text-neutral-500" />
    </button>
  );
}
